import { Controller } from "@hotwired/stimulus"

// Drives the learner lesson reader: Content/Materials tab switching, the
// per-section "Hide Transcript" toggle, and the sidebar Table of Contents
// (collapsible document sections + scroll-spy active highlight).
export default class extends Controller {
  static targets = ["tab", "panel"]

  connect() {
    this.setupTocSpy()
  }

  disconnect() {
    if (this.tocObserver) this.tocObserver.disconnect()
  }

  // Collapses/expands the document sections nested under a TOC entry.
  toggleSection(event) {
    const button = event.currentTarget
    const entry = button.closest(".academy-toc-entry")
    if (!entry) return

    const collapsed = entry.classList.toggle("is-collapsed")
    button.setAttribute("aria-expanded", String(!collapsed))
  }

  // Highlights the TOC link whose target section is currently in view.
  setupTocSpy() {
    this.tocLinks = Array.from(
      this.element.querySelectorAll(".academy-toc-entry-link, .academy-toc-section-link")
    )
    if (this.tocLinks.length === 0) return

    this.tocLinkByTarget = new Map()
    const targets = []

    this.tocLinks.forEach((link) => {
      const id = (link.getAttribute("href") || "").replace(/^#/, "")
      if (!id) return
      const target = document.getElementById(id)
      if (!target) return
      this.tocLinkByTarget.set(target, link)
      targets.push(target)
    })

    if (targets.length === 0) return

    this.tocObserver = new IntersectionObserver(
      (entries) => this.onTocIntersect(entries),
      { rootMargin: "0px 0px -70% 0px", threshold: 0 }
    )
    targets.forEach((target) => this.tocObserver.observe(target))
  }

  onTocIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      const link = this.tocLinkByTarget.get(entry.target)
      if (!link) return

      this.tocLinks.forEach((other) => other.classList.remove("is-active"))
      link.classList.add("is-active")
    })
  }

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab

    this.tabTargets.forEach((element) => {
      element.classList.toggle("is-active", element.dataset.tab === tab)
    })

    this.panelTargets.forEach((element) => {
      element.hidden = element.dataset.tab !== tab
    })
  }

  toggleTranscript(event) {
    const button = event.currentTarget
    const section = button.closest(".academy-transcript")
    if (!section) return

    const body = section.querySelector(".academy-transcript-body")
    if (!body) return

    const label = button.querySelector("[data-academy-lesson-target='transcriptToggleLabel']")
    const icon = button.querySelector("i")
    const collapsed = body.hasAttribute("hidden")
    const hideLabel = button.dataset.hideLabel || "Hide Transcript"
    const showLabel = button.dataset.showLabel || "Show Transcript"

    if (collapsed) {
      body.removeAttribute("hidden")
      if (label) label.textContent = hideLabel
      if (icon) icon.className = "ph ph-eye-slash"
    } else {
      body.setAttribute("hidden", "")
      if (label) label.textContent = showLabel
      if (icon) icon.className = "ph ph-eye"
    }
  }
}
